import { Component, HostListener, DestroyRef, inject, OnInit, OnDestroy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthStateService } from "../../../core/states/auth-state.service";
import { Router, RouterLink } from "@angular/router";
import { AuthService } from "../../../services/auth.service";
import { AsideService, AsideState } from "../../../core/states/aside-service.service";
import { User } from "../../../models/User";
import { NgClass, NgFor, NgIf } from "@angular/common";
import { BreadcrumbService } from '../../../core/services/breadcrumb/breadcrumb.service';
import { Breadcrumb } from '../../../models/Breadcrumb';
import { TeacherNotificationService, TeacherNotification } from '../../../services/teacher-notification.service';
import { VehicleAlertService, VehicleAlert } from '../../../services/vehicle-alert.service';
import { ConfirmationComponent } from '../../../shared/components/confirmation/confirmation.component';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [
    RouterLink,
    NgIf,
    NgFor,
    NgClass,
    ConfirmationComponent
  ],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.css'
})
export class NavbarComponent implements OnInit, OnDestroy {
  asideState!: AsideState;
  user: User | null = null;
  breadcrumbs: Breadcrumb | null = null;
  isDropdownOpen = false;
  private destroyRef = inject(DestroyRef);

  // Notificaciones de profesores
  isNotificationsDropdownOpen = false;
  notifications: TeacherNotification[] = [];
  unreadCount = 0;
  isTeacher = false;
  isAdmin = false;
  showMarkAllConfirmation = false;
  dismissingNotificationId: number | null = null;

  // Alertas de vehículos (ITV)
  isVehicleAlertsDropdownOpen = false;
  vehicleAlerts: VehicleAlert[] = [];
  vehicleAlertCount = 0;


  private notificationInterval: any = null;
  private vehicleAlertInterval: any = null;

  constructor(public asideService: AsideService,
    public authStateService: AuthStateService,
    private router: Router,
    private authService: AuthService,
    private breadcrumbService: BreadcrumbService,
    private teacherNotificationService: TeacherNotificationService,
    private vehicleAlertService: VehicleAlertService) {
    this.asideService.asideState$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (a: AsideState) => {
          this.asideState = a;
        }
      });

    // Suscribirse a los BehaviorSubjects una sola vez
    this.teacherNotificationService.notifications$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(notifications => this.notifications = notifications);

    this.teacherNotificationService.unreadCount$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(count => this.unreadCount = count);

    this.vehicleAlertService.alerts$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(alerts => this.vehicleAlerts = alerts);

    this.vehicleAlertService.alertCount$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(count => this.vehicleAlertCount = count);

    this.authStateService.currentUser
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(user => {
        this.user = user;
        if (!user) return;

        // Detectar roles
        this.isTeacher = (user?.roles?.includes('teacher')) ||
                        (Array.isArray(user?.profile?.roles) && user.profile.roles.some((r: any) => r.name === 'teacher'));
        this.isAdmin = (user?.roles?.includes('admin')) ||
                      (Array.isArray(user?.profile?.roles) && user.profile.roles.some((r: any) => r.name === 'admin'));

        if (this.isTeacher && !this.notificationInterval) {
          this.teacherNotificationService.loadNotifications();
          this.notificationInterval = setInterval(() => this.teacherNotificationService.loadNotifications(), 5 * 60 * 1000);
        }

        // Alertas de vehículos para todos los usuarios del panel.
        if (!this.vehicleAlertInterval) {
          this.vehicleAlertService.loadAlerts();
          this.vehicleAlertInterval = setInterval(() => this.vehicleAlertService.loadAlerts(), 5 * 60 * 1000);
        }
      });

    this.breadcrumbService.breadcrumb
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        this.breadcrumbs = data;
      });
  }

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    if (this.notificationInterval) clearInterval(this.notificationInterval);
    if (this.vehicleAlertInterval) clearInterval(this.vehicleAlertInterval);
  }

  toggleNotificationsDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.isNotificationsDropdownOpen = !this.isNotificationsDropdownOpen;

    // Cerrar otros dropdowns
    if (this.isNotificationsDropdownOpen) {
      this.isDropdownOpen = false;
      this.isVehicleAlertsDropdownOpen = false;
    }
  }

  markAsReadAndNavigate(notification: TeacherNotification, event: MouseEvent): void {
    event.stopPropagation();

    // Iniciar animación de eliminación
    this.dismissingNotificationId = notification.id;

    // Esperar a que termine la animación (300ms) antes de hacer la petición
    setTimeout(() => {
      // Marcar como leída (esto recargará la lista automáticamente)
      this.teacherNotificationService.markAsRead(notification.id).subscribe();

      // NO navegar - mantener al usuario en la página actual

      // Limpiar el estado de animación
      this.dismissingNotificationId = null;
    }, 300);
  }

  markAllAsRead(event: MouseEvent): void {
    event.stopPropagation();
    this.showMarkAllConfirmation = true;
  }

  confirmMarkAllAsRead(): void {
    this.teacherNotificationService.markAllAsRead().subscribe();
    this.showMarkAllConfirmation = false;
  }

  cancelMarkAllAsRead(): void {
    this.showMarkAllConfirmation = false;
  }

  getPriorityColor(priority: string): string {
    switch (priority) {
      case 'high': return 'text-red-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-gray-600';
      default: return 'text-gray-600';
    }
  }

  getTimeSince(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'Hace un momento';
    if (seconds < 3600) return `Hace ${Math.floor(seconds / 60)} min`;
    if (seconds < 86400) return `Hace ${Math.floor(seconds / 3600)} horas`;
    return `Hace ${Math.floor(seconds / 86400)} días`;
  }

  getActionTypeIcon(actionType: string): string {
    switch (actionType) {
      case 'proof_qualification':
        return `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>`;
      case 'practice_evaluation':
        return `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>`;
      default:
        return `<svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>`;
    }
  }

  getActionTypeColor(actionType: string): string {
    switch (actionType) {
      case 'proof_qualification':
        return 'bg-blue-100 text-blue-600';
      case 'practice_evaluation':
        return 'bg-green-100 text-green-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  }

  getActionTypeLabel(actionType: string): string {
    switch (actionType) {
      case 'proof_qualification':
        return 'Calificar examen';
      case 'practice_evaluation':
        return 'Evaluar práctica';
      default:
        return 'Acción pendiente';
    }
  }

  toggleVehicleAlertsDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.isVehicleAlertsDropdownOpen = !this.isVehicleAlertsDropdownOpen;
    if (this.isVehicleAlertsDropdownOpen) {
      this.isDropdownOpen = false;
      this.isNotificationsDropdownOpen = false;
    }
  }

  navigateToVehicle(alert: VehicleAlert): void {
    this.isVehicleAlertsDropdownOpen = false;
    this.router.navigate(['/dashboard/clasesPracticas/cuadrante']);
  }

  getAlertPriorityClasses(priority: string): string {
    switch (priority) {
      case 'overdue': return 'bg-red-50 border-red-200';
      case 'urgent': return 'bg-amber-50 border-amber-200';
      case 'upcoming': return 'bg-green-50 border-green-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  }

  getAlertBadgeClasses(priority: string): string {
    switch (priority) {
      case 'overdue': return 'bg-red-100 text-red-700';
      case 'urgent': return 'bg-amber-100 text-amber-700';
      case 'upcoming': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  }

  getAlertBadgeLabel(priority: string): string {
    switch (priority) {
      case 'overdue': return 'Caducada';
      case 'urgent': return 'Urgente';
      case 'upcoming': return 'Próxima';
      default: return '';
    }
  }

  // Escuchar clics en el documento
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const dropdowns = document.querySelectorAll('.notifications-dropdown, .user-dropdown, .vehicle-alerts-dropdown');

    let clickedInside = false;
    dropdowns.forEach(dropdown => {
      if (dropdown && dropdown.contains(target)) {
        clickedInside = true;
      }
    });

    if (!clickedInside) {
      this.isDropdownOpen = false;
      this.isNotificationsDropdownOpen = false;
      this.isVehicleAlertsDropdownOpen = false;
    }
  }

  // Alternar el menú desplegable
  toggleDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.isDropdownOpen = !this.isDropdownOpen;
    if (this.isDropdownOpen) {
      this.isNotificationsDropdownOpen = false;
      this.isVehicleAlertsDropdownOpen = false;
    }
  }
  logOut() {
    this.authService.logOut().subscribe({
      next: value => {
        this.authStateService.setUser(null);
        this.router.navigate(['/login'])
      }, error: () => {
        this.authStateService.setUser(null);
        this.router.navigate(['/login'])
      }
    })

  }
}
